import { ReactNode } from "react";
import { twMerge } from "tailwind-merge";

// Depth-based spacer widths inserted between the arrow and icon to indent
// content while keeping the arrow at the leftmost position.
// depth 1: one arrow-width (1.75rem), depth 2: two arrow-widths (3.5rem)
const DEPTH_SPACER: Record<number, string> = {
  1: "1.75rem",
  2: "3.5rem",
};

interface StepRowProps {
  arrow?: ReactNode;
  icon?: ReactNode;
  label: ReactNode;
  labelClassName?: string;
  script?: ReactNode;
  count?: ReactNode;
  meta?: ReactNode;
  duration?: ReactNode | null;
  className?: string;
  onClick?: () => void;
  depth?: number;
}

export function StepRow({
  arrow,
  icon,
  label,
  labelClassName,
  script,
  count,
  meta,
  duration,
  className,
  onClick,
  depth = 0,
}: StepRowProps) {
  return (
    <div
      className={twMerge("flex w-full items-center", className)}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? -1 : undefined}
    >
      {/* Zone 1: Fixed — arrow + spacer + icon */}
      <div className="flex w-7 shrink-0 items-center justify-center">{arrow}</div>
      {DEPTH_SPACER[depth] && <div className="shrink-0" style={{ width: DEPTH_SPACER[depth] }} />}
      <div className="flex w-6 shrink-0 items-center justify-center pl-px">{icon}</div>

      {/* Zone 2: Flexible — label + script + count */}
      <div className="ml-1.5 flex min-w-0 flex-auto items-center gap-2 self-stretch overflow-hidden py-1 text-left">
        <div
          className={twMerge(
            "text-charcoal-700 max-w-full shrink-0 truncate font-medium group-hover/link:opacity-100 group-aria-[current=step]/link:opacity-100 group-data-[step-state='waiting_for_dependencies']/link:opacity-45 group-data-[step-state=ignored]/link:opacity-45",
            labelClassName,
          )}
        >
          {label}
        </div>
        {count && <span className="shrink-0">{count}</span>}
        {script && (
          <span className="container-hidden @2xl/list:inline @2xl/list:max-w-[100ch] @4xl/list:max-w-[150ch] text-xs-mono min-w-0 truncate font-mono text-gray-800">
            {script}
          </span>
        )}
      </div>

      {/* Zone 3: Right-aligned — meta + duration */}
      {(meta || duration !== undefined) && (
        <div className="container-hidden @xs/list:inline-flex ml-auto shrink-0 items-center gap-4 self-stretch pl-4 text-sm">
          {meta}
          {duration !== undefined && (
            <span className="text-charcoal-300 whitespace-nowrap text-right tabular-nums">
              {duration ?? "--"}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
